import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, timeout } from 'rxjs';
import { BaseDataStore } from './base-data.store';
import { ExternalLinkStore } from './external-link.store';
import { LoggerService } from '../logger.service';
import { IntelliCatchUrlReachability } from '../../models/copilot/intellicatch-reachability.enum';
import * as AppConstants from '../../modules/catch-core/data/constants/app.constants';

/**
 * Whether the organisation's IntelliCatch URL answers. The header chat button and the shared
 * Generate Tests rule both read this state, so the HEAD probe runs once per origin instead of once
 * per surface, and every consumer sees the same answer.
 */
@Injectable({
  providedIn: 'root',
})
export class IntelliCatchReachabilityStore extends BaseDataStore {
  private _reachability: BehaviorSubject<IntelliCatchUrlReachability> = new BehaviorSubject<IntelliCatchUrlReachability>(IntelliCatchUrlReachability.Unknown);
  public $reachability: Observable<IntelliCatchUrlReachability> = this._reachability.asObservable();

  private resolvedUrl = '';
  private probedOrigin = '';
  private readonly outcomeByOrigin = new Map<string, IntelliCatchUrlReachability>();
  private className = 'IntelliCatchReachabilityStore';

  constructor(
    private externalLinkStore: ExternalLinkStore,
    private http: HttpClient,
    private loggerService: LoggerService,
  ) {
    super();
    this.behaviorSubjectList.push(this._reachability);

    this.subscribers.intelliCatchExternalLink = this.externalLinkStore.$firstExternalLink.subscribe((externalLink) => {
      this.setUrl(AppConstants.findIntelliCatchLink(externalLink?.externalLinks)?.url);
    });
  }

  public get reachability(): IntelliCatchUrlReachability {
    return this._reachability.getValue() ?? IntelliCatchUrlReachability.Unknown;
  }

  public get isReachable(): boolean {
    return this.reachability === IntelliCatchUrlReachability.Reachable;
  }

  // The external link store re-emits on every load and every save, so the probe is keyed on what it
  // is probing rather than on how often it is told: an unchanged URL, or one that only changed path,
  // answers from the outcome already held for that origin.
  private setUrl(url: string | null | undefined): void {
    const resolvedUrl = (url ?? '').trim();
    if (resolvedUrl.toLowerCase() === this.resolvedUrl.toLowerCase()) {
      return;
    }
    this.resolvedUrl = resolvedUrl;

    if (!resolvedUrl) {
      this.probedOrigin = '';
      this.publish(IntelliCatchUrlReachability.Unknown);
      return;
    }

    const origin = this.toOrigin(resolvedUrl);
    if (!origin) {
      this.probedOrigin = '';
      this.publish(IntelliCatchUrlReachability.Unreachable);
      return;
    }
    if (origin === this.probedOrigin) {
      return;
    }
    this.probedOrigin = origin;

    const cachedOutcome = this.outcomeByOrigin.get(origin);
    if (cachedOutcome) {
      this.publish(cachedOutcome);
      return;
    }

    this.publish(IntelliCatchUrlReachability.Checking);
    this.probe(origin);
  }

  private probe(origin: string): void {
    if (!origin) {
      return;
    }
    if (this.subscribers.intelliCatchProbe) {
      this.subscribers.intelliCatchProbe.unsubscribe();
    }

    this.loggerService?.traceInfo(this.className, 'probe', 'Probing IntelliCatch URL', '', origin);
    this.subscribers.intelliCatchProbe = this.http
      .head(origin, { observe: 'response' })
      .pipe(
        timeout(AppConstants.INTELLICATCH_URL_PROBE_TIMEOUT_MS),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        const outcome = response ? IntelliCatchUrlReachability.Reachable : IntelliCatchUrlReachability.Unreachable;
        this.outcomeByOrigin.set(origin, outcome);
        if (origin === this.probedOrigin) {
          this.publish(outcome);
        }
      });
  }

  private toOrigin(url: string | null | undefined): string {
    const candidate = (url ?? '').trim();
    if (!candidate) {
      return '';
    }
    try {
      return new URL(candidate).origin;
    } catch {
      this.loggerService?.logWarning(this.className, 'toOrigin', 'IntelliCatch URL could not be parsed', '', candidate);
      return '';
    }
  }

  private publish(reachability: IntelliCatchUrlReachability): void {
    if (!reachability || this._reachability.getValue() === reachability) {
      return;
    }
    this._reachability.next(reachability);
  }
}
